/**
 * The "Course" pills in each composer (deck, worksheet, booklet, quiz).
 *
 * It only means something on a GCSE science, so it shows when the subject and
 * level in its own composer make that true and hides, and empties, otherwise:
 * a Foundation choice left behind from a Combined Science deck must not travel
 * with the next History one. The server drops a value for a course without one
 * as well (utils/tier.js), so this is a convenience for the teacher, not the
 * only guard.
 *
 * What it offers depends on the subject:
 *   Combined Science          Foundation or Higher
 *   Biology, Chemistry,       Triple (the separate-science GCSE, the default),
 *   Physics                   or the same subject taught as Combined Science,
 *                             at Foundation or Higher
 * Triple has no value of its own: it is the course Biology already is.
 *
 * It sits in the composer's top row beside the level, as pills rather than a
 * dropdown, because it changes the questions as much as the level does and a
 * teacher switching a resource between Foundation and Higher should not have
 * to go looking for it. The hidden select keeps the value, so everything that
 * reads `$('ws-tier').value` is unchanged; a pill sets the select and fires
 * change on it.
 *
 * The subject and level fields are set from script as often as by hand (the
 * subject is guessed from the topic, tiles set the level), which fires no
 * event a listener could rely on, so the state is re-read on a short timer.
 * It is a few string comparisons per composer.
 */
(function () {
    'use strict';

    // Keep in step with utils/tier.js.
    const COMBINED = 'combinedscience';
    const DISCIPLINES = ['biology', 'chemistry', 'physics'];
    const key = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

    // `pills` are what the teacher sees; the select also keeps '' for the
    // Combined Science case, where no pill lit means "not specified".
    const OPTIONS = {
        combined: {
            options: [['', 'Not specified'], ['foundation', 'Foundation'], ['higher', 'Higher']],
            pills: [['foundation', 'Foundation'], ['higher', 'Higher']],
        },
        discipline: {
            options: [
                ['', 'Triple (separate sciences)'],
                ['combined-foundation', 'Combined Science, Foundation'],
                ['combined-higher', 'Combined Science, Higher'],
            ],
            pills: [['', 'Triple'], ['combined-foundation', 'Combined Foundation'], ['combined-higher', 'Combined Higher']],
        },
    };

    // "ws-tier" sits with "ws-subject" and "ws-level"; the deck's own is "tier".
    const idsFor = (tierId) => {
        const prefix = tierId.replace(/tier$/, '');
        return { subject: `${prefix}subject`, level: `${prefix}level` };
    };

    function choose(select, value) {
        if (select.value === value) return;
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function setOptions(select, kind) {
        if (select.dataset.tierKind === kind) return;
        select.dataset.tierKind = kind;
        const spec = OPTIONS[kind];
        select.replaceChildren(...spec.options.map(([value, text]) => {
            const o = document.createElement('option');
            o.value = value;
            o.textContent = text;
            return o;
        }));
        const seg = select.closest('[data-tier-field]').querySelector('.tier-seg');
        if (!seg) return;
        seg.replaceChildren(...spec.pills.map(([value, text]) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'tier-pill';
            b.setAttribute('role', 'radio');
            b.dataset.value = value;
            b.textContent = text;
            b.addEventListener('click', () => {
                // On Combined Science a second click on the lit pill clears it,
                // since "not specified" has no pill of its own.
                const off = kind === 'combined' && select.value === value;
                choose(select, off ? '' : value);
                paint(select);
            });
            return b;
        }));
    }

    function paint(select) {
        const seg = select.closest('[data-tier-field]')?.querySelector('.tier-seg');
        if (!seg) return;
        seg.querySelectorAll('.tier-pill').forEach((b) => {
            const on = b.dataset.value === select.value;
            b.setAttribute('aria-checked', on ? 'true' : 'false');
            b.classList.toggle('is-on', on);
        });
    }

    function sync(select) {
        const ids = idsFor(select.id);
        const subject = document.getElementById(ids.subject);
        const level = document.getElementById(ids.level);
        const field = select.closest('[data-tier-field]');
        if (!field) return;
        const k = subject ? key(subject.value) : '';
        const gcse = !!level && String(level.value).trim().toLowerCase() === 'gcse';
        const kind = !gcse ? null : k === COMBINED ? 'combined' : DISCIPLINES.includes(k) ? 'discipline' : null;
        if (kind) {
            const before = select.value;
            setOptions(select, kind);
            // A value from the other kind's list (Foundation, then Biology is
            // typed over Combined Science) is not in this one and is dropped.
            if (before && [...select.options].some((o) => o.value === before)) select.value = before;
            // Rebuilding the options already emptied it; listeners still need to hear.
            else if (before) select.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (select.value) {
            choose(select, '');
        }
        if (field.hidden === !!kind) field.hidden = !kind;
        if (kind) paint(select);
    }

    function start() {
        const selects = Array.from(document.querySelectorAll('select[data-tier-select]'));
        if (!selects.length) return;
        const run = () => selects.forEach(sync);
        run();
        setInterval(run, 400);
        document.addEventListener('change', run, true);
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
})();
